/**
 * 内容识别缩放对话框（M4-01）。输入目标宽高，其余交给引擎。
 */
import type { JSX } from 'solid-js';
import { Show, createEffect, createSignal } from 'solid-js';
import { useFocusTrap } from '../a11y/focus-trap';
import styles from './SeamCarveDialog.module.css';

export interface SeamCarveDialogProps {
  readonly open: boolean;
  readonly currentWidth: number;
  readonly currentHeight: number;
  readonly busy?: boolean | undefined;
  readonly onClose: () => void;
  readonly onApply: (width: number, height: number) => void;
}

export function SeamCarveDialog(props: SeamCarveDialogProps): JSX.Element {
  const [width, setWidth] = createSignal(props.currentWidth);
  const [height, setHeight] = createSignal(props.currentHeight);
  let dialogRef: HTMLDivElement | undefined;
  useFocusTrap(() => props.open, () => dialogRef ?? null, props.onClose);

  // Solid 组件函数体只跑一次——不像 React 那样每次渲染重新求值，
  // 所以 createSignal(props.currentWidth) 只是「挂载那一刻」的快照。
  // 装配层用同一个组件实例贯穿整个应用生命周期（main.tsx 里没有 <Show> 包一层
  // 才渲染 <SeamCarveDialog>），文档在挂载之后才真正打开时，这两个初始信号
  // 永远停在挂载时的默认尺寸——每次打开都要用当前文档尺寸重置一遍。
  createEffect(() => {
    if (props.open) {
      setWidth(props.currentWidth);
      setHeight(props.currentHeight);
    }
  });

  return (
    <Show when={props.open}>
      <div class={styles.backdrop} onClick={props.onClose} data-testid="seam-carve-backdrop">
        <div
          class={styles.dialog}
          role="dialog"
          ref={(el) => (dialogRef = el)}
          aria-label="内容识别缩放"
          data-testid="seam-carve-dialog"
          onClick={(e) => e.stopPropagation()}
        >
          <h2 class={styles.title}>内容识别缩放</h2>
          <label class={styles.row}>
            <span class={styles.label}>宽度</span>
            <input
              type="number"
              min="1"
              value={width()}
              data-testid="seam-carve-width"
              onInput={(e) => setWidth(Number(e.currentTarget.value))}
            />
          </label>
          <label class={styles.row}>
            <span class={styles.label}>高度</span>
            <input
              type="number"
              min="1"
              value={height()}
              data-testid="seam-carve-height"
              onInput={(e) => setHeight(Number(e.currentTarget.value))}
            />
          </label>
          <div class={styles.actions}>
            <button type="button" class={styles.secondary} onClick={props.onClose}>
              取消
            </button>
            <button
              type="button"
              class={styles.primary}
              data-testid="seam-carve-apply"
              disabled={props.busy === true}
              onClick={() => props.onApply(width(), height())}
            >
              {props.busy === true ? '计算中…' : '应用'}
            </button>
          </div>
        </div>
      </div>
    </Show>
  );
}
